import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { hasPermission, permissionsForRoles, type Permission, type UserRole } from '@blagodar/shared';
import { api, clearAuth, loadAuth, loadLastUserId, onAuthChange, saveAuth, saveLastUserId, type StoredAuth } from '../api/client';
import { countOwnOutbox, handOverDevice } from '../offline/db';

interface AuthContextValue {
  user: StoredAuth['user'] | null;
  roles: UserRole[];
  permissions: Set<Permission>;
  can: (permission: Permission) => boolean;
  login: (phone: string, password: string) => Promise<void>;
  /** Возвращает число неотправленных операций, оставшихся на планшете. */
  logout: () => Promise<number>;
  /** Пароль этого человека известен посторонним — надо сменить */
  weakPassword: boolean;
  /** Убрать предупреждение после смены пароля */
  clearWeakPassword: () => void;
  loading: boolean;
}

/**
 * Экспортируется ради стенда без входа (`dev/app.html`): он даёт
 * контексту сотрудника с ролью из адреса, не входя и не храня сессии.
 * В программе провайдер один — `AuthProvider` ниже.
 */
export const AuthContext = createContext<AuthContextValue | null>(null);
export type { AuthContextValue };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<StoredAuth | null>(() => loadAuth());
  const [loading, setLoading] = useState(false);
  /*
   * ПАМЯТЬ ОТВЕТОВ СЕРВЕРА — ТОГО, КТО ВОШЁЛ (01.10.2026). Ответы помнятся
   * полминуты и дольше, а планшет и компьютер переходят из рук в руки:
   * следующий видел карточки, списки и права прошлого человека, пока экран
   * сам не переспросит (правило 37 про ту же беду в базе планшета). На
   * выданной расходке это кончалось вечной загрузкой: вошли отделом
   * документов — а карточка «свежая», кладовщикова, и форма её не собирала.
   */
  const queryClient = useQueryClient();

  useEffect(() => onAuthChange(setAuth), []);

  const login = useCallback(async (phone: string, password: string) => {
    setLoading(true);
    try {
      const result = await api.post<StoredAuth>('/auth/login', {
        phone,
        password,
        device: navigator.userAgent.slice(0, 180),
      });

      // Планшет переходит от одного замерщика к другому — чужие наряды и
      // черновики не должны остаться в локальной базе. А несданные замеры
      // прошлого — должны: они помечаются его и уйдут при его входе
      // (правило 37), стереть их значит потерять его работу за день.
      //
      // Сравниваем с ОТДЕЛЬНОЙ памятью о последнем входе, а не с токеном:
      // токен пропадает при выходе, и после «Выйти» сравнивать было не с
      // чем — следующий человек открывал планшет с чужими замерами.
      const previousUserId = loadLastUserId();
      if (previousUserId && previousUserId !== result.user.id) {
        await handOverDevice(previousUserId);
      }

      saveLastUserId(result.user.id);
      saveAuth(result);
      queryClient.clear();
      setAuth(result);
    } finally {
      setLoading(false);
    }
  }, [queryClient]);

  /**
   * Выход. Возвращает число неотправленных операций, если они есть.
   *
   * Локальную базу здесь НЕ трогаем: в очереди могут лежать завершённые
   * замеры, которые ещё не уехали на сервер, и стереть их значит потерять
   * работу за день. Чужое подчистится при входе следующего человека.
   */
  const logout = useCallback(async () => {
    const current = loadAuth();
    // Своё: о несданном прошлого человека выходящему говорить незачем
    const pending = await countOwnOutbox(current?.user.id).catch(() => 0);

    try {
      if (current) await api.post('/auth/logout', { refreshToken: current.refreshToken });
    } catch {
      // Выйти нужно в любом случае, даже если сервер недоступен
    }
    clearAuth();
    queryClient.clear();
    setAuth(null);
    return pending;
  }, [queryClient]);

  /**
   * Убрать предупреждение после смены пароля.
   *
   * Спросить сервер второй раз нельзя: пароля у нас больше нет, а
   * сравнивает он введённую строку, а не хеш. Поэтому признак снимается
   * на месте — смена пароля и есть доказательство, что старого больше
   * не существует.
   */
  const clearWeakPassword = useCallback(() => {
    setAuth((current) => {
      if (!current?.weakPassword) return current;
      const next = { ...current, weakPassword: false };
      saveAuth(next);
      return next;
    });
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const roles = (auth?.user.roles ?? []) as UserRole[];
    const permissions = permissionsForRoles(roles);
    return {
      user: auth?.user ?? null,
      roles,
      permissions,
      can: (permission: Permission) => hasPermission(roles, permission),
      login,
      logout,
      loading,
      weakPassword: auth?.weakPassword === true,
      clearWeakPassword,
    };
  }, [auth, login, logout, loading, clearWeakPassword]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth должен вызываться внутри AuthProvider');
  return ctx;
}
